import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import '../styles/fonts.css';

const processSteps = [
  { title: 'Referral Process from individual, health practitioners, family, and friends', description: 'We support you every step of the way as you begin your restorative therapy journey.' },
  { title: 'Assessment — Our team assesses individual needs and follow ups with goals.', description: 'We begin with a comprehensive assessment of your current health and wellness goals.' },
  { title: 'Care Plan Development - Collaboration with families ', description: 'Our experts create a customized restorative therapy plan tailored to your unique needs.' },
  { title: 'Participation & Monitoring — Ongoing progress and reviews', description: 'Regular check-ins and adjustments ensure you stay on track toward your goals.' },
];

export default function ProcessSection() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '0%']);

  return (
    <section id="process" className="relative py-24 overflow-hidden">
      {/* Parallax PNG Background */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: backgroundY }}
      >
        <img 
          // src="https://plus.unsplash.com/premium_photo-1724372238961-8cba54c007c3?w=1920&q=80"
          src="/images/butterflies.jpeg" 
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-purple-100/90" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl pt-2 mx-auto px-6">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-emerald-600 font-medium text-sm uppercase tracking-wider">How It Works</span>
          <h2 className="text-4xl md:text-5xl font-bold kaushan-script  text-gray-900 mt-3 mb-6">
            Our <span className="text-emerald-600">Process</span>
          </h2>
          <p className="text-gray-700 text-md fuzzy-bubbles max-w-2xl mx-auto">
            A simple, supportive approach to help you achieve lasting wellness results.
          </p>
        </motion.div>

        <div className="space-y-6">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.title}
              className="flex items-start gap-4 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl "
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-emerald-500 to-violet-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900  mb-1">{step.title}</h3>
                <p className="text-gray-600 league-spartan-400">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Education and Family Resources */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-3xl md:text-4xl kaushan-script font-bold text-gray-900 mb-4">
            Education & Family <span className="text-emerald-600">Resources</span>
          </h3>
          <p className="text-gray-800 fuzzy-bubbles text-lg max-w-4xl mx-auto">
            Restorative Connections help individuals regain independence through structured, purposeful activity. It’s not just about exercise; it’s about restoring confidence, hope, and dignity.
            <br/><br/>
            Restorative Connections is in partnerships and collaboration with <span class="font-bold text-xl text-ul text-violet-900"> <a href="https://www.communicationconnections.ca" target="_blank"><u>Communication Connections</u> </a> </span> who provides training and certification in Restorative Therapy Program.
          </p>
        </motion.div>
      </div>
    </section>
  );
}